import {useEffect, useLayoutEffect, useRef} from 'react';
import {
  Conversation,
  ConversationMessageAdded,
} from '@stripe-internal/conversations-client';
import {THUMBS_DOWN_ADVANCE_OPTION_ID} from '../helpers/message-added';
import {useMessagesTopAnchored} from '../helpers/useMessagesTopAnchored';
import {smoothScrollToWithBezier} from '../helpers/smoothScrollTo';

const scrollToTargetValue = 0;
const scrollToDuration = 800;
const scrollToEasingCurve = [0.16, 1, 0.3, 1]; // cubic-bezier values
const programmaticScrollDurationBufferMs = 100;
const immediateProgrammaticScrollDurationMs = 50;
const groupedMessagesSelector = '.uh-conversations-grouped-messages';
const userGroupedMessagesSelector =
  '.uh-conversations-grouped-messages[data-group-owner="user"]';
const userAnchorSelector = [
  '.uh-conversations-text-message-row[data-message-type="user"]',
  '.uh-conversations-button-options-message-row[data-message-type="user"]',
  '.uh-conversations-inline-data-message-row[data-message-type="user"]',
  '.uh-conversations-chat-row[data-message="user"]',
].join(', ');

function getElementOffsetTop(
  logRect: DOMRect | undefined,
  elementRect: DOMRect | undefined,
) {
  if (!logRect || !elementRect) {
    return null;
  }
  return Math.round(elementRect.top - logRect.top);
}

function getGroupedMessageElementById(
  contentElement: HTMLDivElement | null,
  groupId: string | null,
) {
  if (!contentElement || !groupId) {
    return null;
  }

  const groupedRows = contentElement.querySelectorAll<HTMLElement>(
    groupedMessagesSelector,
  );

  return (
    Array.from(groupedRows).find(
      (groupedRow) => groupedRow.getAttribute('data-group-id') === groupId,
    ) ?? null
  );
}

function getLastUserRowElement(anchorContainerElement: HTMLElement | null) {
  if (!anchorContainerElement) {
    return null;
  }

  const userRows =
    anchorContainerElement.querySelectorAll<HTMLElement>(userAnchorSelector);

  return userRows[userRows.length - 1] ?? null;
}

function getAnchorElementForGroup(groupedElement: HTMLElement | null) {
  if (!groupedElement) {
    return null;
  }

  return getLastUserRowElement(groupedElement) ?? groupedElement;
}

function getLastUserAnchorElement(contentElement: HTMLDivElement | null) {
  if (!contentElement) {
    return null;
  }

  const lastUserRow = getLastUserRowElement(contentElement);

  if (lastUserRow) {
    return lastUserRow;
  }

  const userGroups = contentElement.querySelectorAll<HTMLElement>(
    userGroupedMessagesSelector,
  );
  const lastUserGroup = userGroups[userGroups.length - 1] ?? null;

  if (lastUserGroup) {
    return getAnchorElementForGroup(lastUserGroup);
  }

  const groupedRows = contentElement.querySelectorAll<HTMLElement>(
    groupedMessagesSelector,
  );

  return getAnchorElementForGroup(
    groupedRows[groupedRows.length - 2] ??
      groupedRows[groupedRows.length - 1] ??
      null,
  );
}

function getAnchorTop(
  logElement: HTMLDivElement | null,
  anchorElement: HTMLElement | null,
) {
  if (!logElement) {
    return null;
  }

  return getElementOffsetTop(
    logElement.getBoundingClientRect(),
    anchorElement?.getBoundingClientRect(),
  );
}

function getAnchorGroupId(anchorElement: HTMLElement | null) {
  return (
    anchorElement?.getAttribute('data-group-id') ??
    anchorElement
      ?.closest<HTMLElement>(groupedMessagesSelector)
      ?.getAttribute('data-group-id') ??
    null
  );
}

function clearProgrammaticScroll({
  programmaticScrollTimeoutRef,
  isProgrammaticScrollRef,
}: {
  programmaticScrollTimeoutRef: React.MutableRefObject<
    ReturnType<typeof setTimeout> | undefined
  >;
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
}) {
  if (programmaticScrollTimeoutRef.current) {
    clearTimeout(programmaticScrollTimeoutRef.current);
    programmaticScrollTimeoutRef.current = undefined;
  }

  isProgrammaticScrollRef.current = false;
}

function markProgrammaticScroll({
  durationMs,
  programmaticScrollTimeoutRef,
  isProgrammaticScrollRef,
}: {
  durationMs: number;
  programmaticScrollTimeoutRef: React.MutableRefObject<
    ReturnType<typeof setTimeout> | undefined
  >;
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
}) {
  clearProgrammaticScroll({
    programmaticScrollTimeoutRef,
    isProgrammaticScrollRef,
  });
  isProgrammaticScrollRef.current = true;
  programmaticScrollTimeoutRef.current = setTimeout(() => {
    isProgrammaticScrollRef.current = false;
    programmaticScrollTimeoutRef.current = undefined;
  }, durationMs);
}

function resetLockedAnchor({
  lockedAnchorElementRef,
  lockedAnchorGroupIdRef,
  lockedAnchorTopRef,
  shouldResetLockedAnchorTopRef,
}: {
  lockedAnchorElementRef: React.MutableRefObject<HTMLElement | null>;
  lockedAnchorGroupIdRef: React.MutableRefObject<string | null>;
  lockedAnchorTopRef: React.MutableRefObject<number | null>;
  shouldResetLockedAnchorTopRef: React.MutableRefObject<boolean>;
}) {
  lockedAnchorElementRef.current = null;
  lockedAnchorGroupIdRef.current = null;
  lockedAnchorTopRef.current = null;
  shouldResetLockedAnchorTopRef.current = true;
}

function suspendLockedAnchor({
  programmaticScrollTimeoutRef,
  isProgrammaticScrollRef,
  isLockedAnchorSuspendedRef,
  lockedAnchorElementRef,
  lockedAnchorGroupIdRef,
  lockedAnchorTopRef,
  shouldResetLockedAnchorTopRef,
}: {
  programmaticScrollTimeoutRef: React.MutableRefObject<
    ReturnType<typeof setTimeout> | undefined
  >;
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
  isLockedAnchorSuspendedRef: React.MutableRefObject<boolean>;
  lockedAnchorElementRef: React.MutableRefObject<HTMLElement | null>;
  lockedAnchorGroupIdRef: React.MutableRefObject<string | null>;
  lockedAnchorTopRef: React.MutableRefObject<number | null>;
  shouldResetLockedAnchorTopRef: React.MutableRefObject<boolean>;
}) {
  clearProgrammaticScroll({
    programmaticScrollTimeoutRef,
    isProgrammaticScrollRef,
  });
  isLockedAnchorSuspendedRef.current = true;
  resetLockedAnchor({
    lockedAnchorElementRef,
    lockedAnchorGroupIdRef,
    lockedAnchorTopRef,
    shouldResetLockedAnchorTopRef,
  });
}

function resumeLockedAnchor({
  isLockedAnchorSuspendedRef,
  lockedAnchorElementRef,
  lockedAnchorGroupIdRef,
  lockedAnchorTopRef,
  shouldResetLockedAnchorTopRef,
}: {
  isLockedAnchorSuspendedRef: React.MutableRefObject<boolean>;
  lockedAnchorElementRef: React.MutableRefObject<HTMLElement | null>;
  lockedAnchorGroupIdRef: React.MutableRefObject<string | null>;
  lockedAnchorTopRef: React.MutableRefObject<number | null>;
  shouldResetLockedAnchorTopRef: React.MutableRefObject<boolean>;
}) {
  isLockedAnchorSuspendedRef.current = false;
  resetLockedAnchor({
    lockedAnchorElementRef,
    lockedAnchorGroupIdRef,
    lockedAnchorTopRef,
    shouldResetLockedAnchorTopRef,
  });
}

export function resolveLockedUserAnchorElement({
  contentElement,
  lockedAnchorElement,
  lockedAnchorGroupId,
  shouldReset,
}: {
  contentElement: HTMLDivElement | null;
  lockedAnchorElement: HTMLElement | null;
  lockedAnchorGroupId: string | null;
  shouldReset: boolean;
}) {
  if (!shouldReset) {
    const lockedGroupElement = getGroupedMessageElementById(
      contentElement,
      lockedAnchorGroupId,
    );

    if (lockedGroupElement) {
      return getAnchorElementForGroup(lockedGroupElement);
    }

    if (lockedAnchorElement?.isConnected) {
      return lockedAnchorElement;
    }
  }

  return getLastUserAnchorElement(contentElement);
}

export function MessageScroller({
  conversation,
  logRef,
  contentRef,
}: {
  conversation: Conversation;
  logRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
}) {
  const {shouldTopAnchor, hasTopAnchorFlag} =
    useMessagesTopAnchored(conversation);
  const lockedAnchorElementRef = useRef<HTMLElement | null>(null);
  const lockedAnchorGroupIdRef = useRef<string | null>(null);
  const lockedAnchorTopRef = useRef<number | null>(null);
  const shouldResetLockedAnchorTopRef = useRef(true);
  const isLockedAnchorSuspendedRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  // This effect performs the "scroll" animation to move the message
  // to the top of the screen. It only does this with a few types of
  // messages, such a user-sent text message.
  useEffect(() => {
    if (!shouldTopAnchor) {
      clearProgrammaticScroll({
        programmaticScrollTimeoutRef,
        isProgrammaticScrollRef,
      });
      isLockedAnchorSuspendedRef.current = false;
      resetLockedAnchor({
        lockedAnchorElementRef,
        lockedAnchorGroupIdRef,
        lockedAnchorTopRef,
        shouldResetLockedAnchorTopRef,
      });
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;

    const smoothScrollToTop = () => {
      if (logRef.current) {
        markProgrammaticScroll({
          durationMs: scrollToDuration + programmaticScrollDurationBufferMs,
          programmaticScrollTimeoutRef,
          isProgrammaticScrollRef,
        });
        smoothScrollToWithBezier(
          logRef.current,
          scrollToTargetValue,
          scrollToDuration,
          ...scrollToEasingCurve,
        );
      }
    };
    // Note: the "initial" prompt from the user that the widget opens
    // with doesn't come through here because it's a message that
    // already exists. That's a good thing! We don't want to scroll
    // down when the widget opens because we show a greeting message
    // that we don't want to hide. All future messages will scrolll
    // down.
    const sub = conversation.eventsObservable().subscribe((e) => {
      if (e.name === 'conversation-message-added') {
        const addedEvent = e as ConversationMessageAdded;

        if (
          addedEvent.message?.type === 'text-conversation-message' ||
          addedEvent.message?.type ===
            'text-with-attachments-conversation-message'
        ) {
          if (addedEvent.participantId === conversation.user.id) {
            resumeLockedAnchor({
              isLockedAnchorSuspendedRef,
              lockedAnchorElementRef,
              lockedAnchorGroupIdRef,
              lockedAnchorTopRef,
              shouldResetLockedAnchorTopRef,
            });
            // New user message, scroll to it
            clearTimeout(timer);
            timer = setTimeout(smoothScrollToTop, 50);
          }
        }
      } else if (e.name === 'conversation-option-selected') {
        if (e.optionId === THUMBS_DOWN_ADVANCE_OPTION_ID) {
          clearTimeout(timer);
          suspendLockedAnchor({
            programmaticScrollTimeoutRef,
            isProgrammaticScrollRef,
            isLockedAnchorSuspendedRef,
            lockedAnchorElementRef,
            lockedAnchorGroupIdRef,
            lockedAnchorTopRef,
            shouldResetLockedAnchorTopRef,
          });
          return;
        }
        resumeLockedAnchor({
          isLockedAnchorSuspendedRef,
          lockedAnchorElementRef,
          lockedAnchorGroupIdRef,
          lockedAnchorTopRef,
          shouldResetLockedAnchorTopRef,
        });
        clearTimeout(timer);
        timer = setTimeout(smoothScrollToTop, 50);
      } else if (e.name === 'conversation-trigger-scroll-to-top') {
        resumeLockedAnchor({
          isLockedAnchorSuspendedRef,
          lockedAnchorElementRef,
          lockedAnchorGroupIdRef,
          lockedAnchorTopRef,
          shouldResetLockedAnchorTopRef,
        });
        clearTimeout(timer);
        timer = setTimeout(smoothScrollToTop, 50);
      }
    });

    return () => {
      clearTimeout(timer);
      clearProgrammaticScroll({
        programmaticScrollTimeoutRef,
        isProgrammaticScrollRef,
      });
      sub.unsubscribe();
    };
  }, [conversation, shouldTopAnchor, logRef]);

  // It's important to dereference these so the effects below actually
  // rerun when they change
  const logElement = logRef.current;
  const contentElement = contentRef.current;

  useEffect(() => {
    if (!shouldTopAnchor || !logElement) {
      return;
    }

    const handleUserScrollIntent = () => {
      if (isLockedAnchorSuspendedRef.current) {
        return;
      }

      suspendLockedAnchor({
        programmaticScrollTimeoutRef,
        isProgrammaticScrollRef,
        isLockedAnchorSuspendedRef,
        lockedAnchorElementRef,
        lockedAnchorGroupIdRef,
        lockedAnchorTopRef,
        shouldResetLockedAnchorTopRef,
      });
    };

    const handleScroll = () => {
      if (
        isProgrammaticScrollRef.current ||
        isLockedAnchorSuspendedRef.current
      ) {
        return;
      }

      suspendLockedAnchor({
        programmaticScrollTimeoutRef,
        isProgrammaticScrollRef,
        isLockedAnchorSuspendedRef,
        lockedAnchorElementRef,
        lockedAnchorGroupIdRef,
        lockedAnchorTopRef,
        shouldResetLockedAnchorTopRef,
      });
    };

    logElement.addEventListener('wheel', handleUserScrollIntent, {
      passive: true,
    });
    logElement.addEventListener('touchmove', handleUserScrollIntent, {
      passive: true,
    });
    logElement.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      logElement.removeEventListener('wheel', handleUserScrollIntent);
      logElement.removeEventListener('touchmove', handleUserScrollIntent);
      logElement.removeEventListener('scroll', handleScroll);
    };
  }, [conversation, shouldTopAnchor, logElement]);

  // This effect makes sure the CSS variable stays up to date with the height of
  // the the log. We need this when calculating the required extra space so we
  // can scroll messages to the top.
  useLayoutEffect(() => {
    if (!hasTopAnchorFlag) {
      return;
    }

    const obs = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const {height} = entry.contentRect;
        logElement?.style.setProperty('--log-height', `${height}px`);
      });
    });

    if (logElement) {
      obs.observe(logElement);
    }

    return () => {
      obs.disconnect();
    };
  }, [hasTopAnchorFlag, logElement]);

  // This effect implements a type of "scroll locking". Because of how
  // we implement the log using a flex item with `column-reverse`,
  // a "scroll" of 0 is at the bottom of the element. New content will
  // immediately appear at the bottom. We want to "lock" the scrolling
  // to the message that we moved up so new content appears below it.
  // To lock it we track the height with a resize observer and change
  // the `scrollTop` to avoid any visual changes
  useLayoutEffect(() => {
    if (!shouldTopAnchor) {
      return;
    }

    let firstEvent = true;

    const obs = new ResizeObserver((entries) => {
      if (firstEvent) {
        firstEvent = false;
        return;
      }

      entries.forEach((_entry) => {
        if (isLockedAnchorSuspendedRef.current) {
          return;
        }

        const currentAnchorElementBefore = resolveLockedUserAnchorElement({
          contentElement,
          lockedAnchorElement: lockedAnchorElementRef.current,
          lockedAnchorGroupId: lockedAnchorGroupIdRef.current,
          shouldReset: shouldResetLockedAnchorTopRef.current,
        });
        const shouldRelockAnchor =
          shouldResetLockedAnchorTopRef.current ||
          currentAnchorElementBefore == null;
        const currentAnchorTopBefore = getAnchorTop(
          logElement,
          currentAnchorElementBefore,
        );

        if (
          currentAnchorElementBefore &&
          currentAnchorElementBefore !== lockedAnchorElementRef.current
        ) {
          lockedAnchorElementRef.current = currentAnchorElementBefore;
        }

        if (
          currentAnchorElementBefore &&
          lockedAnchorGroupIdRef.current == null
        ) {
          lockedAnchorGroupIdRef.current = getAnchorGroupId(
            currentAnchorElementBefore,
          );
        }

        if (shouldRelockAnchor && currentAnchorTopBefore != null) {
          lockedAnchorElementRef.current = currentAnchorElementBefore;
          lockedAnchorGroupIdRef.current = getAnchorGroupId(
            currentAnchorElementBefore,
          );
          lockedAnchorTopRef.current = currentAnchorTopBefore;
          shouldResetLockedAnchorTopRef.current = false;
        }

        const anchorDelta =
          lockedAnchorTopRef.current != null && currentAnchorTopBefore != null
            ? Math.round(currentAnchorTopBefore - lockedAnchorTopRef.current)
            : null;

        if (logElement && anchorDelta != null && anchorDelta !== 0) {
          markProgrammaticScroll({
            durationMs: immediateProgrammaticScrollDurationMs,
            programmaticScrollTimeoutRef,
            isProgrammaticScrollRef,
          });
          logElement.scrollTop += anchorDelta;
        }
      });
    });

    if (contentElement) {
      obs.observe(contentElement);
    }

    return () => {
      obs.disconnect();
    };
  }, [conversation, shouldTopAnchor, contentElement, logElement]);
  return null;
}
